import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { formatDistanceToNow } from 'date-fns';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Loader2, ArrowUp, ArrowDown, Filter } from 'lucide-react';
import { notify } from '@/components/ui/notify';
import { AccountFinal } from '@/api/entities';
import { createPageUrl } from '@/utils';
import { sanitizeId } from '@/components/utils/id';

const API_BASE_URL =
  window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'
    ? 'https://api.neogtm.com'
    : 'https://api.neogtm.com';

const PAGE_SIZE = 200;

const DEFAULT_REASON_CODES = [
  'no_fit',
  'no_budget',
  'wrong_timing',
  'competitor_chosen',
  'no_response',
  'contacted_externally',
  'duplicate',
  'other',
];

const REASON_LABELS = {
  no_fit: 'Not a fit',
  no_budget: 'No budget',
  wrong_timing: 'Wrong timing',
  competitor_chosen: 'Competitor chosen',
  no_response: 'No response',
  contacted_externally: 'Contacted externally',
  duplicate: 'Duplicate',
  other: 'Other',
};

function accountFinalUrl(accountId) {
  const id = sanitizeId(accountId);
  if (!id) return null;
  return createPageUrl(`AccountFinal?id=${encodeURIComponent(id)}`);
}

function pctFit(score) {
  const n = Number(score);
  if (!Number.isFinite(n)) return null;
  return n <= 1 ? n * 100 : n;
}

function fitScoreClass(score) {
  const pct = pctFit(score);
  if (pct == null) return 'text-gray-400';
  if (pct >= 90) return 'text-green-700 font-semibold tabular-nums';
  if (pct >= 75) return 'text-blue-700 font-semibold tabular-nums';
  if (pct >= 60) return 'text-amber-700 font-semibold tabular-nums';
  return 'text-red-700 font-semibold tabular-nums';
}

function formatFitTwoDecimals(score) {
  const n = Number(score);
  if (!Number.isFinite(n)) return '—';
  return n.toFixed(2);
}

function formatUpdatedAt(iso) {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '—';
    return formatDistanceToNow(d, { addSuffix: true });
  } catch {
    return '—';
  }
}

export default function DisengagedTab({ tenantId }) {
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [availableReasons, setAvailableReasons] = useState(DEFAULT_REASON_CODES);
  const [migrationPending, setMigrationPending] = useState(false);
  const [sortDir, setSortDir] = useState('asc');
  const [reengageTarget, setReengageTarget] = useState(null);
  const [reengageBusy, setReengageBusy] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const [typeFilter, setTypeFilter] = useState('all');
  const [typeOptions, setTypeOptions] = useState([]);

  useEffect(() => {
    setOffset(0);
  }, [tenantId, typeFilter]);

  useEffect(() => {
    let cancelled = false;
    const loadTypeOptions = async () => {
      if (!tenantId) {
        setTypeOptions([]);
        return;
      }
      try {
        const rows = await AccountFinal.list();
        if (cancelled) return;
        const sectors = new Set();
        (Array.isArray(rows) ? rows : []).forEach((acc) => {
          if (acc?.tenant_id !== tenantId && acc?.tenant_id !== 'global') return;
          const sector = String(acc?.sector || '').trim();
          if (sector) sectors.add(sector);
        });
        setTypeOptions(Array.from(sectors).sort((a, b) => a.localeCompare(b)));
      } catch {
        if (!cancelled) setTypeOptions([]);
      }
    };
    void loadTypeOptions();
    return () => { cancelled = true; };
  }, [tenantId]);

  const fetchList = useCallback(async () => {
    if (!tenantId) return;
    setLoading(true);
    setLoadError(false);
    try {
      const params = new URLSearchParams({
        tenant_id: tenantId,
        limit: String(PAGE_SIZE),
        offset: String(offset),
      });
      if (typeFilter !== 'all') params.append('sector', typeFilter);
      const res = await fetch(`${API_BASE_URL}/api/accounts/disengaged?${params.toString()}`, {
        credentials: 'include',
      });
      if (!res.ok) {
        setRows([]);
        setTotal(0);
        setLoadError(true);
        return;
      }
      const data = await res.json();
      const rowList = Array.isArray(data.rows) ? data.rows : [];
      const totalN = Number(data.total) || 0;
      if (rowList.length === 0 && offset > 0 && totalN > 0) {
        const desiredOffset = Math.floor((totalN - 1) / PAGE_SIZE) * PAGE_SIZE;
        if (desiredOffset !== offset) {
          setOffset(desiredOffset);
          return;
        }
      }
      setRows(rowList);
      setTotal(totalN);
      setMigrationPending(data.migration_pending === true);
      if (Array.isArray(data.available_reasons) && data.available_reasons.length > 0) {
        setAvailableReasons(data.available_reasons);
      } else {
        setAvailableReasons(DEFAULT_REASON_CODES);
      }
    } catch {
      setRows([]);
      setTotal(0);
      setLoadError(true);
    } finally {
      setLoading(false);
    }
  }, [tenantId, offset, typeFilter]);

  useEffect(() => {
    void fetchList();
  }, [fetchList]);

  const sortedRows = useMemo(() => {
    let copy = [...rows];
    if (typeFilter !== 'all') {
      copy = copy.filter((row) => String(row?.sector || '').trim() === typeFilter);
    }
    copy.sort((a, b) => {
      const an = String(a?.account_name || '').toLowerCase();
      const bn = String(b?.account_name || '').toLowerCase();
      if (an < bn) return sortDir === 'asc' ? -1 : 1;
      if (an > bn) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });
    return copy;
  }, [rows, sortDir, typeFilter]);

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const currentPage = Math.floor(offset / PAGE_SIZE) + 1;

  const toggleNameSort = () => {
    setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
  };

  const patchDisengage = async (accountId, body) => {
    const id = sanitizeId(accountId);
    if (!id) throw new Error('missing id');
    const res = await fetch(`${API_BASE_URL}/api/accounts/${encodeURIComponent(id)}/disengage`, {
      method: 'PATCH',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    let data = null;
    try {
      data = await res.json();
    } catch {
      data = null;
    }
    if (!res.ok) {
      const msg = data?.error || data?.message || res.statusText || 'Request failed';
      throw new Error(msg);
    }
    return data;
  };

  const handleReasonChange = async (row, newReason) => {
    if (migrationPending || !tenantId) return;
    const prevReason = row.is_visible_reason;
    if (newReason === prevReason) return;

    setRows((rs) =>
      rs.map((r) =>
        r.account_id === row.account_id ? { ...r, is_visible_reason: newReason } : r
      )
    );
    try {
      const data = await patchDisengage(row.account_id, {
        tenant_id: tenantId,
        is_visible: false,
        reason: newReason,
      });
      setRows((rs) =>
        rs.map((r) =>
          r.account_id === row.account_id
            ? {
                ...r,
                is_visible_reason: data?.is_visible_reason ?? newReason,
                updated_at: data?.updated_at ?? r.updated_at,
              }
            : r
        )
      );
    } catch (e) {
      setRows((rs) =>
        rs.map((r) =>
          r.account_id === row.account_id ? { ...r, is_visible_reason: prevReason } : r
        )
      );
      notify({
        title: 'Could not update reason',
        description: e?.message || 'Try again.',
        variant: 'destructive',
      });
    }
  };

  const confirmReengage = async () => {
    const row = reengageTarget;
    if (!row || !tenantId) return;
    setReengageBusy(true);
    try {
      await patchDisengage(row.account_id, { tenant_id: tenantId, is_visible: true });
      setReengageTarget(null);
      await fetchList();
    } catch (e) {
      notify({
        title: 'Could not re-engage',
        description: e?.message || 'Try again.',
        variant: 'destructive',
      });
    } finally {
      setReengageBusy(false);
    }
  };

  if (!tenantId) {
    return null;
  }

  if (loading && rows.length === 0 && total === 0) {
    return (
      <Card>
        <CardContent className="p-10 flex items-center justify-center">
          <Loader2 className="w-6 h-6 animate-spin text-[#00C2C2]" />
        </CardContent>
      </Card>
    );
  }

  if (loadError) {
    return (
      <Card>
        <CardContent className="p-8 text-sm text-gray-600">
          Could not load disengaged accounts. Check your connection and try again.
        </CardContent>
      </Card>
    );
  }

  if (!loading && total === 0) {
    return (
      <Card>
        <CardContent className="p-8 text-sm text-gray-600 max-w-xl">
          No disengaged accounts. You will see accounts here once you mark them as not a fit, no budget,
          etc., from the account profile or this tab.
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      {migrationPending && (
        <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
          Reason tracking is being deployed. Reasons will populate once the next backend release lands.
        </div>
      )}

      <Card>
        <CardContent className="p-0">
          <div className="flex flex-wrap items-center justify-between gap-2 px-4 py-3 border-b text-sm text-gray-600">
            <span>
              <strong>{total.toLocaleString()}</strong> disengaged account{total === 1 ? '' : 's'}
            </span>
            <div className="flex flex-wrap items-center gap-2">
              <Select value={typeFilter} onValueChange={setTypeFilter}>
                <SelectTrigger className="w-full sm:w-[180px] h-9 text-sm">
                  <Filter className="w-4 h-4 mr-2 shrink-0" />
                  <SelectValue placeholder="Type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All types</SelectItem>
                  {typeOptions.map((sector) => (
                    <SelectItem key={sector} value={sector}>
                      {sector}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            {totalPages > 1 && (
              <div className="flex items-center gap-2">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  disabled={offset <= 0 || loading}
                  onClick={() => setOffset((o) => Math.max(0, o - PAGE_SIZE))}
                >
                  Previous
                </Button>
                <span className="text-xs tabular-nums">
                  Page {currentPage} of {totalPages}
                </span>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  disabled={offset + PAGE_SIZE >= total || loading}
                  onClick={() => setOffset((o) => o + PAGE_SIZE)}
                >
                  Next
                </Button>
              </div>
            )}
            </div>
          </div>

          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>
                  <button
                    type="button"
                    className="inline-flex items-center gap-1 font-medium hover:text-[#00A3A3]"
                    onClick={toggleNameSort}
                  >
                    Account name
                    {sortDir === 'asc' ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />}
                  </button>
                </TableHead>
                <TableHead>Sector</TableHead>
                <TableHead>Country</TableHead>
                <TableHead className="text-right">Fit score</TableHead>
                <TableHead>Reason</TableHead>
                <TableHead>Last updated</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {sortedRows.map((row) => {
                const href = accountFinalUrl(row.account_id);
                const currentReason = row.is_visible_reason ? String(row.is_visible_reason) : '';
                const reasonOptions = [...new Set([...availableReasons, currentReason].filter(Boolean))];
                return (
                  <TableRow key={row.account_id}>
                    <TableCell className="font-medium max-w-[240px]">
                      <div className="truncate">
                        {href ? (
                          <Link
                            to={href}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-[#0A1A2F] hover:text-[#00A3A3] hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-[#00C2C2] rounded-sm"
                          >
                            {row.account_name || '—'}
                          </Link>
                        ) : (
                          row.account_name || '—'
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="text-gray-700">{row.sector || '—'}</TableCell>
                    <TableCell className="text-gray-700">{row.country || '—'}</TableCell>
                    <TableCell className={`text-right ${fitScoreClass(row.account_fit_score)}`}>
                      {formatFitTwoDecimals(row.account_fit_score)}
                    </TableCell>
                    <TableCell className="min-w-[180px]">
                      {migrationPending ? (
                        <span className="text-gray-400">—</span>
                      ) : (
                        <Select
                          value={currentReason || undefined}
                          onValueChange={(v) => void handleReasonChange(row, v)}
                          disabled={migrationPending}
                        >
                          <SelectTrigger className="h-8 text-xs">
                            <SelectValue placeholder="Set reason…" />
                          </SelectTrigger>
                          <SelectContent>
                            {reasonOptions.map((code) => (
                              <SelectItem key={code} value={code}>
                                {REASON_LABELS[code] || code}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      )}
                    </TableCell>
                    <TableCell className="text-sm text-gray-600 whitespace-nowrap">
                      {formatUpdatedAt(row.updated_at)}
                    </TableCell>
                    <TableCell className="text-right">
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className="text-xs"
                        onClick={() => setReengageTarget(row)}
                      >
                        Re-engage
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <AlertDialog open={!!reengageTarget} onOpenChange={(open) => !open && setReengageTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Re-engage account?</AlertDialogTitle>
            <AlertDialogDescription>
              Re-engage {reengageTarget?.account_name || 'this account'}? It will reappear in playbook flows and Most
              Active analytics.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={reengageBusy}>Cancel</AlertDialogCancel>
            <Button type="button" disabled={reengageBusy} onClick={() => void confirmReengage()}>
              {reengageBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Confirm'}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
